import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import SystemMetrics from '../components/SystemMetrics';
import { Routes, Route, NavLink, useNavigate, Navigate } from 'react-router-dom';
import SkinLibrary from './SkinLibrary';
import SkinEditor from './SkinEditor';
import TexturePackLibrary from './TexturePackLibrary';
import TextureEditor from './TextureEditor';

// ===================================================
// Helper
// ===================================================

function formatUptime(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

// ===================================================
// Sub-Seiten (Route-Komponenten)
// ===================================================

function PlayerHome() {
  const [servers, setServers] = useState([]);
  const [modpacks, setModpacks] = useState([]);
  const [whitelistStatuses, setWhitelistStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBlueMap, setShowBlueMap] = useState(null);
  const [followPlayer, setFollowPlayer] = useState(null);
  const [playerPositions, setPlayerPositions] = useState({});
  const bluemapIframeRef = useRef(null);
  const positionPollRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(async () => {
      try {
        const data = await api.getServers();
        setServers(data);
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (servers.length > 0) loadWhitelistStatuses();
  }, [servers]);

  const pollPlayerPositions = useCallback(async (server) => {
    if (!server || !server.bluemapPort) return;
    try {
      const res = await fetch(`/api/bluemap/${server.id}/maps/world/live/players.json`);
      if (res.ok) {
        const data = await res.json();
        const positions = {};
        if (data.players) {
          for (const p of data.players) {
            if (p.name && p.position) positions[p.name] = { x: p.position.x, y: p.position.y, z: p.position.z };
          }
        }
        setPlayerPositions(positions);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (showBlueMap && showBlueMap.bluemapPort) {
      pollPlayerPositions(showBlueMap);
      positionPollRef.current = setInterval(() => pollPlayerPositions(showBlueMap), 5000);
    }
    return () => {
      if (positionPollRef.current) { clearInterval(positionPollRef.current); positionPollRef.current = null; }
    };
  }, [showBlueMap, pollPlayerPositions]);

  useEffect(() => {
    if (followPlayer && playerPositions[followPlayer] && bluemapIframeRef.current) {
      const pos = playerPositions[followPlayer];
      const hash = `#world:${Math.round(pos.x)}:${Math.round(pos.z)}:${Math.round(pos.y)}:100:0.6:0:0:0:perspective`;
      try {
        bluemapIframeRef.current.contentWindow.location.hash = hash;
      } catch {
        const baseUrl = `/api/bluemap/${showBlueMap.id}/`;
        if (baseUrl) bluemapIframeRef.current.src = baseUrl + hash;
      }
    }
  }, [followPlayer, playerPositions, showBlueMap]);

  async function loadData() {
    try {
      const [srvs, packs] = await Promise.all([api.getServers(), api.getModpacks()]);
      setServers(srvs);
      setModpacks(packs.filter(p => p.type === 'curseforge'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadWhitelistStatuses() {
    try {
      const data = await api.getMyWhitelistStatus();
      const map = {};
      if (Array.isArray(data)) { for (const entry of data) map[entry.serverId] = entry; }
      setWhitelistStatuses(map);
    } catch {}
  }

  const runningServers = servers.filter(s => s.status === 'running');
  const totalPlayers = servers.reduce((sum, s) => sum + (s.players?.length || 0), 0);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;

  if (showBlueMap && showBlueMap.bluemapPort) {
    return (
      <div className="bluemap-viewer-container">
        <div className="bluemap-viewer-header">
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowBlueMap(null); setFollowPlayer(null); setPlayerPositions({}); }}>
            &lt; Zurueck
          </button>
          <h2>{showBlueMap.name} - Weltkarte</h2>
          <span className={`status-badge ${showBlueMap.status}`}><span className="status-dot" />Online</span>
        </div>
        {showBlueMap.players && showBlueMap.players.length > 0 && (
          <div className="follow-controls">
            <span className="follow-label">Spieler folgen:</span>
            <div className="follow-player-list">
              {showBlueMap.players.map(name => (
                <button
                  key={name}
                  className={`follow-player-btn ${followPlayer === name ? 'active' : ''}`}
                  onClick={() => setFollowPlayer(p => p === name ? null : name)}
                >
                  {name}
                  {playerPositions[name] && (
                    <span className="follow-coords">({Math.round(playerPositions[name].x)}, {Math.round(playerPositions[name].z)})</span>
                  )}
                </button>
              ))}
            </div>
            {followPlayer && <button className="btn btn-secondary btn-sm" onClick={() => setFollowPlayer(null)}>Folgen beenden</button>}
          </div>
        )}
        <div className="bluemap-viewer-frame">
          <iframe ref={bluemapIframeRef} src={`/api/bluemap/${showBlueMap.id}/`} title={`BlueMap - ${showBlueMap.name}`} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header"><h2>Server-Uebersicht</h2></div>
      <div className="grid grid-3">
        <div className="stat-card"><div className="stat-icon">&#127981;</div><div className="stat-value">{servers.length}</div><div className="stat-label">Server gesamt</div></div>
        <div className="stat-card"><div className="stat-icon">&#9989;</div><div className="stat-value">{runningServers.length}</div><div className="stat-label">Server online</div></div>
        <div className="stat-card"><div className="stat-icon">&#128100;</div><div className="stat-value">{totalPlayers}</div><div className="stat-label">Spieler online</div></div>
      </div>
      <div style={{ marginTop: 24 }}>
        {servers.length === 0 ? (
          <div className="empty-state"><div className="icon">&#127981;</div><p>Noch keine Server konfiguriert</p></div>
        ) : (
          <div className="guest-server-list">
            {servers.map(server => {
              const isRunning = server.status === 'running';
              const playerCount = server.players?.length || 0;
              const hasBluemap = server.bluemapPort && isRunning;
              const pack = modpacks.find(p => p.id === server.modpackId);
              const wlStatus = whitelistStatuses[server.id];
              return (
                <div key={server.id} className="guest-server-item">
                  <div className="server-card">
                    <div className="flex items-center justify-between">
                      <span className="server-name">{server.name}</span>
                      <div className="flex items-center gap-2">
                        {isRunning && playerCount > 0 && <span className="player-count-badge">{playerCount} Spieler</span>}
                        <span className={`status-badge ${server.status}`}><span className="status-dot" />{isRunning ? 'Online' : server.status === 'stopped' ? 'Offline' : server.status}</span>
                      </div>
                    </div>
                    <div className="server-meta">
                      <span>Spieler: {playerCount}</span>
                      {server.uptime > 0 && <span>Uptime: {formatUptime(server.uptime)}</span>}
                      {pack && <span style={{ color: '#17d553' }}>Modpack: {pack.name}</span>}
                    </div>
                    {wlStatus && (
                      <div style={{ marginTop: 8 }}>
                        {wlStatus.whitelisted ? (
                          <span className="whitelist-status-badge whitelisted">&#9989; Auf der Whitelist</span>
                        ) : wlStatus.pending ? (
                          <span className="whitelist-status-badge wl-pending">&#9203; Anfrage ausstehend</span>
                        ) : (
                          <span className="whitelist-status-badge not-whitelisted">&#9888; Nicht auf der Whitelist</span>
                        )}
                      </div>
                    )}
                    {isRunning && server.players && server.players.length > 0 && (
                      <div className="server-player-names">
                        {server.players.map(name => <span key={name} className="tag">{name}</span>)}
                      </div>
                    )}
                    {hasBluemap && (
                      <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowBlueMap(server)}>
                        &#128506; Weltkarte oeffnen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerChat() {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [chatServerId, setChatServerId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    api.getServers().then(data => {
      setServers(data);
      const running = data.find(s => s.status === 'running');
      setChatServerId(running ? running.id : data[0]?.id || null);
    }).catch(() => {});
  }, []);

  const { connected: wsConnected, chatEntries, chatWatchActive, startChatWatch, stopChatWatch } = useWebSocket(chatServerId);

  useEffect(() => {
    if (chatServerId && user?.username) {
      startChatWatch(chatServerId, user.username);
    }
    return () => { stopChatWatch(); };
  }, [chatServerId, user?.username, startChatWatch, stopChatWatch]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatEntries]);

  return (
    <div>
      <div className="page-header">
        <h2>Chat-Verlauf</h2>
        <div className="flex items-center gap-2">
          <span className={`ws-status-indicator ${wsConnected ? 'connected' : 'disconnected'}`}>
            <span className="ws-status-dot" />{wsConnected ? 'Verbunden' : 'Getrennt'}
          </span>
          {chatWatchActive && <span className="chat-watch-badge">Mitleser aktiv</span>}
        </div>
      </div>
      {servers.length > 1 && (
        <div className="chat-server-selector">
          <label>Server:</label>
          <select value={chatServerId || ''} onChange={e => setChatServerId(e.target.value)} className="mc-select">
            {servers.map(s => <option key={s.id} value={s.id}>{s.name} {s.status === 'running' ? '(Online)' : '(Offline)'}</option>)}
          </select>
        </div>
      )}
      <div className="card chat-card">
        <div className="chat-container player-chat-container">
          {chatEntries.length === 0 ? (
            <div className="empty-state" style={{ padding: 40 }}>
              <p className="text-muted">Noch keine neuen Chat-Nachrichten seit deinem Login.</p>
              <p className="text-sm text-muted" style={{ marginTop: 8 }}>Neue Nachrichten erscheinen hier automatisch.</p>
            </div>
          ) : (
            chatEntries.map((entry, i) => (
              <div key={i} className={`chat-line chat-${entry.type || 'message'}`}>
                <span className="chat-time">{new Date(entry.ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                {entry.type === 'message' ? (
                  <span className="chat-msg"><span className="chat-player">&lt;{entry.player}&gt;</span>{' '}<span className="chat-text">{entry.message}</span></span>
                ) : entry.type === 'join' ? (
                  <span className="chat-msg chat-join">{entry.player} ist dem Spiel beigetreten</span>
                ) : entry.type === 'leave' ? (
                  <span className="chat-msg chat-leave">{entry.player} hat das Spiel verlassen</span>
                ) : (
                  <span className="chat-msg">{entry.message || entry.player}</span>
                )}
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>
      </div>
      <p className="text-sm text-muted" style={{ marginTop: 12 }}>
        Nur lesen &mdash; Nachrichten werden live aktualisiert. Alle Online-Spieler sehen, dass du mitliest.
      </p>
    </div>
  );
}

function PlayerConnection() {
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [modpacks, setModpacks] = useState([]);

  useEffect(() => {
    Promise.all([api.getServers(), api.getModpacks()]).then(([srvs, packs]) => {
      setServers(srvs);
      setModpacks(packs.filter(p => p.type === 'curseforge'));
    }).catch(() => {});
  }, []);

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
  }

  return (
    <div>
      <div className="page-header"><h2>Verbindungsinformationen</h2></div>
      {servers.length === 0 ? (
        <div className="empty-state"><div className="icon">&#128279;</div><p>Noch keine Server konfiguriert</p></div>
      ) : (
        <div className="connection-list">
          {servers.map(server => {
            const address = `minecraft.lexwar.cool:${server.port}`;
            const pack = modpacks.find(p => p.id === server.modpackId);
            const isRunning = server.status === 'running';
            return (
              <div key={server.id} className="card connection-card">
                <div className="card-header">
                  <h3>{server.name}</h3>
                  <span className={`status-badge ${server.status}`}><span className="status-dot" />{isRunning ? 'Online' : 'Offline'}</span>
                </div>
                <div className="connection-address-box">
                  <label>Server-Adresse:</label>
                  <div className="connection-address">
                    <code>{address}</code>
                    <button className="btn btn-primary btn-sm" onClick={() => copyToClipboard(address)}>Kopieren</button>
                  </div>
                </div>
                {pack && (
                  <div className="connection-warning">
                    &#9888;&#65039; Dieser Server benutzt das Modpack <strong>{pack.name}</strong>.
                    Du musst es installiert haben, um dich zu verbinden.
                    Gehe zum <button className="link-btn" onClick={() => navigate('/modpacks')}>Modpacks-Tab</button> fuer die Anleitung.
                  </div>
                )}
                <div className="connection-guide">
                  <h4>So verbindest du dich:</h4>
                  <ol>
                    <li>Starte Minecraft {pack ? 'mit dem installierten Modpack' : ''}</li>
                    <li>Klicke auf <strong>"Mehrspieler"</strong></li>
                    <li>Klicke auf <strong>"Server hinzufuegen"</strong></li>
                    <li>Gib als Servername ein: <code>{server.name}</code></li>
                    <li>Gib als Serveradresse ein: <code>{address}</code></li>
                    <li>Klicke auf <strong>"Fertig"</strong></li>
                    <li>Waehle den Server aus und klicke <strong>"Server beitreten"</strong></li>
                  </ol>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlayerModpacks() {
  const [servers, setServers] = useState([]);
  const [modpacks, setModpacks] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    Promise.all([api.getServers(), api.getModpacks()]).then(([srvs, packs]) => {
      setServers(srvs);
      setModpacks(packs.filter(p => p.type === 'curseforge'));
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="page-header"><h2>Modpacks & Installation</h2></div>
      {modpacks.length === 0 ? (
        <div className="empty-state">
          <div className="icon">&#128230;</div>
          <p>Keine Modpacks verfuegbar</p>
          <p className="text-sm text-muted">Der Admin hat noch keine Modpacks konfiguriert.</p>
        </div>
      ) : (
        <div className="modpack-list">
          {modpacks.map(pack => {
            const usedByServers = servers.filter(s => s.modpackId === pack.id);
            return (
              <div key={pack.id} className="card modpack-card">
                <div className="card-header">
                  <h3>{pack.name} {pack.version && <span className="text-muted" style={{ fontSize: 18 }}>v{pack.version}</span>}</h3>
                </div>
                {pack.description && <p className="text-muted" style={{ fontSize: 18 }}>{pack.description}</p>}
                <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                  {pack.mcVersion && <span className="tag">MC {pack.mcVersion}</span>}
                  {pack.loaderType && <span className="tag">{pack.loaderType} {pack.loaderVersion}</span>}
                  <span className="tag">{pack.mods?.length || 0} Mods</span>
                </div>
                {usedByServers.length > 0 && (
                  <div className="text-sm mt-2" style={{ color: '#d4c483' }}>Benutzt von: {usedByServers.map(s => s.name).join(', ')}</div>
                )}
                <div className="mt-4">
                  <a className="btn btn-primary" href={api.getModpackExportUrl(pack.id)} target="_blank" rel="noreferrer">
                    &#128229; Modpack herunterladen (ZIP)
                  </a>
                </div>
                <div className="prism-guide">
                  <h4>Installation mit Prism Launcher</h4>
                  <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
                    Prism Launcher ist ein kostenloser, quelloffener Minecraft-Launcher.{' '}
                    <a href="https://prismlauncher.org/download/" target="_blank" rel="noreferrer" className="link-text">Download: prismlauncher.org</a>
                  </p>
                  <ol className="prism-steps">
                    <li><strong>Modpack herunterladen</strong> — Klicke oben auf "Modpack herunterladen (ZIP)" und speichere die Datei.<img src="/install-guide/1.png" alt="Modpack herunterladen" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                    <li><strong>Prism Launcher oeffnen</strong> — Starte Prism Launcher und klicke auf <strong>"Instanz erstellen"</strong>.<img src="/install-guide/2.png" alt="Prism Launcher - Instanz erstellen" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                    <li><strong>Import waehlen</strong> — Waehle links <strong>"Importieren"</strong> aus.<img src="/install-guide/3.png" alt="Importieren auswaehlen" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                    <li><strong>ZIP auswaehlen</strong> — Klicke auf "Durchsuchen" und waehle die heruntergeladene ZIP-Datei aus.<img src="/install-guide/4.png" alt="Durchsuchen klicken" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                    <li><strong>Installieren</strong> — Klicke auf <strong>"OK"</strong> und warte, bis die Installation abgeschlossen ist.<img src="/install-guide/5.png" alt="OK klicken" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                    <li><strong>Starten</strong> — Doppelklicke auf die neue Instanz, um Minecraft mit dem Modpack zu starten.<img src="/install-guide/6.png" alt="Instanz starten" className="install-screenshot" onClick={e => setLightboxImg(e.target.src)} /></li>
                  </ol>
                  <p className="text-sm text-muted" style={{ marginTop: 12 }}>
                    Video-Anleitung:{' '}
                    <a href="https://www.youtube.com/watch?v=UovlzR_RSd4" target="_blank" rel="noreferrer" className="link-text">YouTube: Prism Launcher Modpack Import</a>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {lightboxImg && (
        <div className="screenshot-lightbox" onClick={() => setLightboxImg(null)}>
          <img src={lightboxImg} alt="Vergroesserte Ansicht" />
        </div>
      )}
    </div>
  );
}

function PlayerRules() {
  const [rules, setRules] = useState(null);
  useEffect(() => {
    api.getRules().then(setRules).catch(() => {});
  }, []);

  return (
    <div>
      <div className="page-header"><h2>{rules?.title || 'Regeln & Informationen'}</h2></div>
      <div className="card">
        <div className="rules-content">
          {rules?.content ? (
            rules.content.split('\n').map((line, i) => (
              <p key={i} style={{ marginBottom: line.trim() === '' ? 12 : 4 }}>{line || '\u00A0'}</p>
            ))
          ) : (
            <p className="text-muted">Noch keine Regeln oder Informationen hinterlegt.</p>
          )}
        </div>
        {rules?.updatedAt && (
          <div className="text-sm text-muted mt-4">
            Zuletzt aktualisiert: {new Date(rules.updatedAt).toLocaleDateString('de-DE')}
            {rules.updatedBy && ` von ${rules.updatedBy}`}
          </div>
        )}
      </div>
    </div>
  );
}

// ===================================================
// Haupt-Komponente
// ===================================================

function PlayerDashboard() {
  const { user, logout } = useAuth();

  return (
    <div className="player-layout">
      <aside className="sidebar player-sidebar">
        <div className="sidebar-header">
          <h1>LexWar's MC Versteck</h1>
        </div>
        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#127968;</span>
            Server-Uebersicht
          </NavLink>
          <NavLink to="/chat" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#128172;</span>
            Chat-Verlauf
          </NavLink>
          <NavLink to="/connection" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#128279;</span>
            Verbindung
          </NavLink>
          <NavLink to="/modpacks" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#128230;</span>
            Modpacks
          </NavLink>
          <NavLink to="/rules" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#128220;</span>
            Regeln & Info
          </NavLink>
          <div className="sidebar-section-label">Kreativ-Werkzeuge</div>
          <NavLink to="/skins" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#128100;</span>
            Skin Editor
          </NavLink>
          <NavLink to="/texture-packs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="icon">&#127912;</span>
            Texture Packs
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <div className="player-user-info">
            <span className="text-sm">&#9876;&#65039; {user?.username}</span>
            <button className="btn btn-secondary btn-sm" onClick={logout}>Abmelden</button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <SystemMetrics />
        <Routes>
          <Route path="/" element={<PlayerHome />} />
          <Route path="/chat" element={<PlayerChat />} />
          <Route path="/connection" element={<PlayerConnection />} />
          <Route path="/modpacks" element={<PlayerModpacks />} />
          <Route path="/rules" element={<PlayerRules />} />
          <Route path="/skins" element={<SkinLibrary />} />
          <Route path="/skins/editor" element={<SkinEditor />} />
          <Route path="/skins/editor/:id" element={<SkinEditor />} />
          <Route path="/texture-packs" element={<TexturePackLibrary />} />
          <Route path="/texture-packs/:id" element={<TextureEditor />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default PlayerDashboard;
